import React, { useMemo } from "react";
import { Box, Tooltip } from "@mui/material";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { fundService } from "../../services/fundService";
import { getColorForPercentileNumber } from "../../utils/fundsFormatter";
import { formatPercentage } from "../../utils/namesFormater";
import { PolicyExposureChanges } from "../../hooks/feeLab/usePathDragChanges";

interface Props {
  mimshakRow: MimshakData;
  cubeSize?: number;
  onCubeClick?: (mimshakRow: MimshakData) => void;
  exposureChanges?: PolicyExposureChanges;
  handlerCode?: string;
  modifiedFundIds?: number[];
}

const PathRatingMiniCubes: React.FC<Props> = ({ mimshakRow, cubeSize = 14, onCubeClick, exposureChanges, handlerCode, modifiedFundIds }) => {
  const buildCube = (fundId: number, isNew: boolean) => {
    const fund = fundService.getFundFromCacheById(fundId);
    const percentile = fund?.percentile_6m_yield as number | undefined;
    const color =
      percentile == null || isNaN(Number(percentile))
        ? "#ccc"
        : getColorForPercentileNumber(Number(percentile));

    const fundName = fund
      ? `${fund.fund_id}-${fund.fund_name}`
      : `מסלול ${fundId}`;

    const yieldValue = fund?.cumulative_6m_yield as number | undefined;

    const tooltipText = [
      fundName,
      yieldValue != null && !isNaN(Number(yieldValue))
        ? `תשואה 6M: ${formatPercentage(Number(yieldValue))}`
        : `תשואה 6M: אין נתון`,
      percentile != null && !isNaN(Number(percentile))
        ? `דירוג 6M: ${percentile}%`
        : "אין נתון דירוג",
    ].join("\n");

    return {
      key: `${mimshakRow.policy_number}-${fundId}`,
      color,
      tooltipText,
      percentile: percentile != null && !isNaN(Number(percentile)) ? Number(percentile) : -1,
      isNew,
    };
  };

  const cubes = useMemo(() => {
    const originalSet = new Set(mimshakRow.fund_ids ?? []);

    if (modifiedFundIds?.length) {
      return modifiedFundIds
        .map((fundId) => buildCube(fundId, !originalSet.has(fundId)))
        .sort((a, b) => b.percentile - a.percentile);
    }

    const fundIds = mimshakRow.fund_ids;
    if (!fundIds || fundIds.length === 0) return [];

    return fundIds
      .map((fundId) => buildCube(fundId, false))
      .sort((a, b) => b.percentile - a.percentile);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mimshakRow.fund_ids, mimshakRow.policy_number, modifiedFundIds]);

  // Build exposure changes tooltip
  const exposureTooltip = useMemo(() => {
    if (!exposureChanges) return null;

    const changes: string[] = ["שינויי חשיפה מבוקשים:"];

    if (exposureChanges.stock_market_percent) {
      const { old_value, new_value } = exposureChanges.stock_market_percent;
      changes.push(`חשיפה למניות: ${formatPercentage(old_value)} ← ${formatPercentage(new_value)}`);
    }

    if (exposureChanges.foreign_currency_exposure_percent) {
      const { old_value, new_value } = exposureChanges.foreign_currency_exposure_percent;
      changes.push(`חשיפה למט"ח: ${formatPercentage(old_value)} ← ${formatPercentage(new_value)}`);
    }

    if (exposureChanges.foreign_exposure_percent) {
      const { old_value, new_value } = exposureChanges.foreign_exposure_percent;
      changes.push(`חשיפה לחו"ל: ${formatPercentage(old_value)} ← ${formatPercentage(new_value)}`);
    }

    return changes.length > 1 ? changes.join("\n") : null;
  }, [exposureChanges]);

  // Detect: handler changed but no funds match the new manufacturer
  const noNewPathSelected = useMemo(() => {
    if (!handlerCode || !modifiedFundIds?.length) return false;
    const hasMatchingFund = modifiedFundIds.some((fundId) => {
      const fund = fundService.getFundFromCacheById(fundId);
      return fund && String(fund.managing_corporation_legal_id) === String(handlerCode);
    });
    return !hasMatchingFund;
  }, [handlerCode, modifiedFundIds]);

  const isNewPolicy = mimshakRow.policy_number?.startsWith("generated-") || mimshakRow.policy_number?.startsWith("transfered-");

  if (cubes.length === 0) {
    if (!isNewPolicy || !onCubeClick) return null;
    return (
      <Tooltip title={<div style={{ whiteSpace: "pre-line", direction: "rtl", textAlign: "right" }}>{"לחץ לבחירת מסלול"}</div>} arrow>
        <Box
          onClick={() => onCubeClick(mimshakRow)}
          sx={{ display: "flex", alignItems: "center", gap: 0.3, cursor: "pointer", "&:hover": { opacity: 0.8 } }}
        >
          <Box sx={{ width: cubeSize, height: cubeSize, borderRadius: "3px", border: "2px solid #bdbdbd", backgroundColor: "transparent" }} />
          <CallSplitIcon sx={{ fontSize: 16, color: "red" }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      onClick={onCubeClick ? () => onCubeClick(mimshakRow) : undefined}
      sx={{
        display: "flex",
        gap: 0.3,
        alignItems: "center",
        justifyContent: "center",
        cursor: onCubeClick ? "pointer" : "default",
        "&:hover": onCubeClick ? { opacity: 0.8 } : undefined,
      }}
    >
      {!noNewPathSelected && cubes.map((cube) => (
        <Tooltip
          key={cube.key}
          title={
            <div style={{ whiteSpace: "pre-line", direction: "rtl", textAlign: "right" }}>
              {cube.tooltipText}
            </div>
          }
          arrow
        >
          <Box sx={{ position: "relative", width: cubeSize, height: cubeSize }}>
            <Box
              sx={{
                width: cubeSize,
                height: cubeSize,
                borderRadius: "3px",
                backgroundColor: cube.color,
              }}
            />
            {cube.isNew && (
              <Box
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  border: "1.5px solid #1976d2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CallSplitIcon sx={{ fontSize: 9, color: "#1976d2" }} />
              </Box>
            )}
          </Box>
        </Tooltip>
      ))}
      {noNewPathSelected && (
        <Tooltip
          title={
            <div style={{ whiteSpace: "pre-line", direction: "rtl", textAlign: "right" }}>
              {"לא נבחר מסלול חדש\nהמסלולים הקיימים אינם מהיצרן הנוכחי"}
            </div>
          }
          arrow
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
            <Box
              sx={{
                width: cubeSize,
                height: cubeSize,
                borderRadius: "3px",
                border: `2px solid ${cubes[0]?.color ?? "#ccc"}`,
                backgroundColor: "transparent",
              }}
            />
            {!exposureTooltip && <CallSplitIcon sx={{ fontSize: 16, color: "red" }} />}
          </Box>
        </Tooltip>
      )}
      {exposureTooltip && (
        <Tooltip
          title={
            <div style={{ whiteSpace: "pre-line", direction: "rtl", textAlign: "right" }}>
              {noNewPathSelected
                ? `לא נבחר מסלול חדש\nהמסלולים הקיימים אינם מהיצרן הנוכחי\n\n${exposureTooltip}`
                : exposureTooltip}
            </div>
          }
          arrow
        >
          <CallSplitIcon
            sx={{
              fontSize: 16,
              color: "#1976d2",
              cursor: "pointer",
              "&:hover": { opacity: 0.7 },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default PathRatingMiniCubes;
